import { Vector3 } from 'three/webgpu';

/**
 * A hollow in uniform rock acts like a sphere of negative mass on a neutral
 * background. With centre c, radius R and surface strength s:
 *   inside  (d < R): g = s * (x - c) / R            (outward, zero at the centre)
 *   outside (d >= R): g = s * R² * (x - c) / d³     (pushes away, inverse square)
 * The total field is the sum over all hollows.
 */
export interface GravitySource {
  readonly center: Vector3;
  readonly radius: number;
  /** Surface gravity at the wall, m/s². */
  readonly strength: number;
  /**
   * Optional: how far into the rock this hollow's push reaches (m). The exterior
   * field fades smoothly to zero over this distance, so hollows joined by a short
   * cave don't distort each other's gravity ("true shapes, tuned strength").
   * Without it the field is the exact inverse-square law.
   */
  readonly reach?: number;
  /**
   * Optional: a torus instead of a sphere. `radius` is then the ring's radius (around the y
   * axis through `center`) and `tube` the tube's. The field is a bent cylinder's: outward
   * from the circle through the tube's middle, growing linearly to `strength` at the wall,
   * and falling off as 1/d outside. That is exact for a thin tube; in a fat one the true
   * negative-mass field tilts a little toward the ring's outside, which this leaves out so
   * the whole wall is level ground.
   */
  readonly tube?: number;
}

/**
 * A place with gravity of its own, like a cave: inside its box it can weaken the hollows'
 * pull and add its own. `apply` writes its own field at p into `out` and returns the share
 * (0..1) of the hollows' field that still applies there.
 */
export interface GravityRegion {
  readonly min: Vector3;
  readonly max: Vector3;
  apply(p: Vector3, out: Vector3): number;
}

/** Contributions weaker than this fraction of 1 g are ignored (outside only). */
const CUTOFF_G = 0.002 * 9.81;

const _own = new Vector3();

export class GravityField {
  readonly sources: GravitySource[] = [];
  readonly regions: GravityRegion[] = [];

  add(source: GravitySource): void {
    this.sources.push(source);
  }

  addRegion(region: GravityRegion): void {
    this.regions.push(region);
  }

  removeRegion(region: GravityRegion): void {
    const i = this.regions.indexOf(region);
    if (i >= 0) this.regions.splice(i, 1);
  }

  /** Field at universe position p, written into out: the hollows', then any region's. */
  sample(p: Vector3, out: Vector3): Vector3 {
    this.sampleHollows(p, out);
    for (const r of this.regions) {
      if (p.x < r.min.x || p.y < r.min.y || p.z < r.min.z || p.x > r.max.x || p.y > r.max.y || p.z > r.max.z)
        continue;
      const keep = r.apply(p, _own);
      out.multiplyScalar(keep).add(_own);
    }
    return out;
  }

  /** The hollows' field alone. */
  sampleHollows(p: Vector3, out: Vector3): Vector3 {
    let gx = 0;
    let gy = 0;
    let gz = 0;
    for (const s of this.sources) {
      let dx = p.x - s.center.x;
      const dy = p.y - s.center.y;
      let dz = p.z - s.center.z;
      if (s.tube !== undefined) {
        // Measure from the nearest point of the circle through the tube's middle.
        const rho = Math.hypot(dx, dz) || 1e-9;
        dx -= (dx / rho) * s.radius;
        dz -= (dz / rho) * s.radius;
        const w2 = dx * dx + dy * dy + dz * dz;
        const t = s.tube;
        let k: number;
        if (w2 < t * t) k = s.strength / t;
        else {
          if (w2 * CUTOFF_G * CUTOFF_G > s.strength * s.strength * t * t) continue;
          k = (s.strength * t) / w2;
          if (s.reach !== undefined) {
            const x = (Math.sqrt(w2) - t) / s.reach;
            if (x >= 1) continue;
            k *= 1 - x * x * (3 - 2 * x);
          }
        }
        gx += k * dx;
        gy += k * dy;
        gz += k * dz;
        continue;
      }
      const d2 = dx * dx + dy * dy + dz * dz;
      const r2 = s.radius * s.radius;
      let k: number;
      if (d2 < r2) {
        k = s.strength / s.radius;
      } else {
        // s·R²/d² < cutoff  <=>  d² > s·R²/cutoff
        if (d2 * CUTOFF_G > s.strength * r2) continue;
        const d = Math.sqrt(d2);
        k = (s.strength * r2) / (d2 * d);
        if (s.reach !== undefined) {
          const x = (d - s.radius) / s.reach;
          if (x >= 1) continue;
          k *= 1 - x * x * (3 - 2 * x);
        }
      }
      gx += k * dx;
      gy += k * dy;
      gz += k * dz;
    }
    return out.set(gx, gy, gz);
  }

  /**
   * Gravitational potential Φ (g = −∇Φ), continuous at every wall:
   *   inside: 3sR/2 − s·d²/(2R);  outside: s·R²/d.
   * Used by tests and for energy-conserving trajectory checks. Ignores `reach`, and
   * torus sources.
   */
  potential(p: Vector3): number {
    let phi = 0;
    for (const s of this.sources) {
      if (s.tube !== undefined) continue;
      const d = p.distanceTo(s.center);
      phi +=
        d < s.radius
          ? 1.5 * s.strength * s.radius - (s.strength * d * d) / (2 * s.radius)
          : (s.strength * s.radius * s.radius) / d;
    }
    return phi;
  }
}

const _g = new Vector3();

/**
 * Integrates a ballistic path through the field (semi-implicit Euler, f64).
 * Writes up to `maxPoints` positions into `out` (a flat array of xyz) and
 * returns how many were written. Stops early when `stop(p)` returns true.
 */
export function integrateTrajectory(
  field: GravityField,
  start: Vector3,
  velocity: Vector3,
  dt: number,
  maxPoints: number,
  out: Float64Array,
  stop?: (p: Vector3) => boolean,
): number {
  const p = start.clone();
  const v = velocity.clone();
  let n = 0;
  for (let i = 0; i < maxPoints; i++) {
    out[n * 3] = p.x;
    out[n * 3 + 1] = p.y;
    out[n * 3 + 2] = p.z;
    n++;
    field.sample(p, _g);
    v.addScaledVector(_g, dt);
    p.addScaledVector(v, dt);
    if (stop?.(p)) break;
  }
  return n;
}
